import { useCallback, useEffect, useState } from 'react'

import { searchCustomers } from '@/lib/api/customers'
import { toE164Phone } from '@/lib/phone'

import {
  findExactCustomerByPhone,
  POS_CUSTOMER_SEARCH_DEBOUNCE_MS,
  shouldSearchCustomers,
} from './posCustomer'

/**
 * @param {object} [options]
 * @param {(customer: import('@/types/api').Customer) => void} [options.onSelect]
 * @param {string} [options.phoneCountry]
 */
export function usePosCustomerSearch({ onSelect, phoneCountry = '+971' } = {}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')

  const phoneE164 = toE164Phone(query, phoneCountry)
  const exactMatch = findExactCustomerByPhone(results, phoneE164)

  useEffect(() => {
    if (!shouldSearchCustomers(query)) {
      setResults([])
      return undefined
    }

    let active = true
    const handle = window.setTimeout(async () => {
      setSearching(true)
      setError('')
      try {
        const customers = await searchCustomers(query.trim(), { limit: 8 })
        if (!active) return
        setResults(customers)
      } catch (err) {
        if (!active) return
        setResults([])
        setError(err?.response?.data?.message ?? 'Unable to search guests.')
      } finally {
        if (active) setSearching(false)
      }
    }, POS_CUSTOMER_SEARCH_DEBOUNCE_MS)

    return () => {
      active = false
      window.clearTimeout(handle)
    }
  }, [query, phoneCountry])

  const reset = useCallback(() => {
    setQuery('')
    setResults([])
    setError('')
  }, [])

  const selectCustomer = useCallback((customer) => {
    onSelect?.(customer)
    reset()
  }, [onSelect, reset])

  return {
    query,
    setQuery,
    results,
    searching,
    error,
    phoneE164,
    exactMatch,
    reset,
    selectCustomer,
  }
}
